/**
 * One-time injected CSS rules that force IMA's injected DOM into a
 * full-cover overlay, so the publisher-supplied ad container actually
 * shows the playing video.
 *
 * What IMA actually injects (verified from a live rewarded show — DOM
 * dump 2026-06-24):
 *
 *   <div class="getjar-ima-surface">         ← OUR ad container
 *     <video id="getjar-…-video">             ← OUR slot (unused — see below)
 *     <div style="position: absolute;          ← IMA's wrapper
 *                 width: 1920px;
 *                 height: 945px;">
 *       <div>
 *         <video src="…ad.mp4">                ← IMA's OWN <video> — this is
 *                                                where the ad actually plays
 *         <div>                                 ← click-through overlay
 *       </div>
 *       <div style="display: none">…</div>     ← preroll/postroll backup
 *       <iframe id="goog_…"                    ← IMA UI (skip button, OMID)
 *               width="1920" height="945"
 *               style="position: relative;
 *                      color-scheme: light;">
 *       <iframe style="visibility: hidden; …">
 *     </div>
 *   </div>
 *
 * Key discovery: the `videoElement` we pass to
 * `new google.ima.AdDisplayContainer(container, videoElement)` is an
 * API contract slot — IMA does NOT play the video into it. IMA creates
 * its OWN `<video>` inside its wrapper. So styling our element with
 * `z-index: 1` + opaque `background` actively HIDES the playing video.
 *
 * Three rules below — each fixes one layer of IMA's injected stack:
 *
 *  1. `> div`  — IMA's outer wrapper is `position: absolute` with
 *     hard-coded pixel dimensions (1920×945). Force it to fill the ad
 *     container so it's responsive and doesn't pin to a stale size.
 *
 *  2. `iframe[id^="goog_"]` (descendant, not direct child — the iframe
 *     is nested inside the wrapper) — IMA's UI iframe is `position:
 *     relative` with `color-scheme: light`, so it sits in-flow at its
 *     hard-coded dimensions and paints an OPAQUE white/black panel
 *     over the playing video. Force absolute overlay + transparent
 *     background + `color-scheme: normal` so the iframe's inner doc
 *     doesn't get an implicit light/dark fill.
 *
 *  3. `video` (descendant) — covers BOTH our slot `<video>` and IMA's
 *     real `<video>`. Forcing fill on both is harmless; IMA's video
 *     already has `width: 100%; height: 100%` inline so the rule
 *     reinforces, and our slot stays consistent with the ad surface.
 *
 * Cross-origin notes:
 *   The iframe's inner document lives on `imasdk.googleapis.com` — we
 *   can't change its body styles from the parent. But the iframe
 *   ELEMENT's `background` + `color-scheme` are on OUR side and
 *   determine the implicit fill rule the iframe content paints over.
 *   Setting `color-scheme: normal` on the iframe tells the browser
 *   "this iframe has no color-scheme preference"; combined with
 *   `background: transparent`, it stops painting the opaque white/dark
 *   panel.
 */
export const IMA_SURFACE_CLASS = 'getjar-ima-surface';
const STYLE_ELEMENT_ID = 'getjar-ima-iframe-overlay-style';
/**
 * Idempotent — call once per renderer instantiation. Subsequent calls
 * are a no-op; the `<style>` tag is identified by its id.
 */
export function ensureImaIframeOverlayStyle() {
    if (typeof document === 'undefined')
        return;
    if (document.getElementById(STYLE_ELEMENT_ID))
        return;
    const styleEl = document.createElement('style');
    styleEl.id = STYLE_ELEMENT_ID;
    styleEl.textContent = `
    /* IMA's outer wrapper — pixel-sized by default (1920×945). Force fill. */
    .${IMA_SURFACE_CLASS} > div {
      position: absolute !important;
      top: 0 !important;
      left: 0 !important;
      inset: 0 !important;
      width: 100% !important;
      height: 100% !important;
    }

    /* IMA's UI iframe (id="goog_…") — overlay transparently so the
       <video> underneath shows through. position: relative + opaque
       color-scheme produced the visible white/black panel that masked
       playback in earlier sessions on this branch. */
    .${IMA_SURFACE_CLASS} iframe[id^="goog_"] {
      position: absolute !important;
      top: 0 !important;
      left: 0 !important;
      inset: 0 !important;
      width: 100% !important;
      height: 100% !important;
      border: 0 !important;
      background: transparent !important;
      color-scheme: normal !important;
    }

    /* Both our slot <video> (passed to AdDisplayContainer for the API
       contract) and IMA's OWN <video> (where the ad actually plays).
       Fill the surface so neither sits at a stale pixel block. */
    .${IMA_SURFACE_CLASS} video {
      position: absolute !important;
      top: 0 !important;
      left: 0 !important;
      inset: 0 !important;
      width: 100% !important;
      height: 100% !important;
    }
  `;
    document.head.appendChild(styleEl);
}
